import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  Link,
  Tooltip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate, useLocation } from "react-router-dom";
import SignInLayout from "../../layouts/SignInLayout";
import { enqueueSnackbar } from "notistack";

const validationSchema = yup.object({
  code: yup
    .string()
    .required("Confirmation code is required")
    .min(6, "Code must be at least 6 characters"),
});

interface LocationState {
  email?: string;
  password?: string; // Password passed from sign-up for auto-sign-in
}

const EmailConfirmation: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [canResend, setCanResend] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  // Extract email and password from location.state
  const state = location.state as LocationState | null;
  const email = state?.email || "";
  const password = state?.password || ""; // For auto-sign-in

  // Clear password from history state for security (optional but recommended)
  useEffect(() => {
    if (password) {
      // Replace current history entry without the password
      window.history.replaceState(
        { ...state, password: undefined },
        document.title
      );
    }
  }, []);

  // Redirect if no email in state (user navigated directly)
  useEffect(() => {
    if (!email) {
      enqueueSnackbar("Please register or sign in first.", {
        variant: "warning",
      });
      navigate("/auth/sign-in", { replace: true });
    }
  }, [email, navigate]);

  // Countdown timer for resend button
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendCountdown]);

  const formik = useFormik({
    initialValues: {
      code: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (!email) {
        enqueueSnackbar("Email is missing. Please try registering again.", {
          variant: "error",
        });
        navigate("/auth/sign-up", { replace: true });
        return;
      }

      setIsSubmitting(true);

      try {
        // Call confirmSignUp with password for auto-sign-in
        const result = await auth.confirmSignUp({
          username: email.trim().toLowerCase(),
          code: values.code.trim(),
          password: password || undefined, // Pass password if available
        });

        // Check if auto-sign-in succeeded
        if (result.signedIn) {
          // User is signed in - AuthContext will handle navigation
          // No need to navigate here, navigateBasedOnRole was called
          return;
        }

        // Confirmation succeeded but not signed in
        // Navigate to sign-in page
        navigate("/auth/sign-in", {
          replace: true,
          state: { email: email.trim().toLowerCase() },
        });
      } catch (error: any) {
        console.error("Confirmation error:", error);

        // Handle specific cases that need UI updates
        const errorCode = error?.code || error?.name || "";

        if (errorCode === "ExpiredCodeException") {
          setCanResend(true);
          setResendCountdown(0);
        } else if (
          errorCode === "NotAuthorizedException" ||
          (errorCode === "InvalidParameterException" &&
            error.message?.includes("confirmed"))
        ) {
          // Account already confirmed - redirect to sign in
          setTimeout(() => {
            navigate("/auth/sign-in", {
              replace: true,
              state: { email },
            });
          }, 2000);
        }

        // AuthContext already shows error snackbar, no need to duplicate
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleResendClick = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (!canResend || !email) return;

    try {
      setCanResend(false);
      setResendCountdown(60);

      await auth.resendSignUp(email.trim().toLowerCase());

      enqueueSnackbar("Verification code resent! Check your email.", {
        variant: "success",
      });
    } catch (error: any) {
      console.error("Error resending verification code:", error);

      enqueueSnackbar(error.message || "Failed to resend verification code.", {
        variant: "error",
      });

      setCanResend(true);
      setResendCountdown(0);
    }
  };

  // Don't render if no email (will redirect)
  if (!email) {
    return null;
  }

  return (
    <SignInLayout>
      <Typography variant="h5" gutterBottom mb={2}>
        Email Confirmation
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        We sent a verification code to <strong>{email}</strong>. Please check
        your inbox and spam folder.
      </Alert>

      <form onSubmit={formik.handleSubmit}>
        <Box mb={2}>
          <TextField
            fullWidth
            id="code"
            name="code"
            label="Confirmation Code"
            placeholder="Enter 6-digit code"
            value={formik.values.code}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.code && Boolean(formik.errors.code)}
            helperText={formik.touched.code && formik.errors.code}
            disabled={isSubmitting}
            autoFocus
            inputProps={{
              maxLength: 10,
              autoComplete: "one-time-code",
            }}
          />
        </Box>

        <Box mb={2}>
          <Button
            color="primary"
            variant="contained"
            fullWidth
            type="submit"
            disabled={isSubmitting || !formik.isValid || !formik.values.code}
            sx={{ py: 1.5 }}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Confirm Account"
            )}
          </Button>
        </Box>

        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Tooltip
              title={
                resendCountdown > 0
                  ? `Wait ${resendCountdown}s before resending`
                  : "Send a new verification code"
              }
              placement="right"
            >
              <span>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={handleResendClick}
                  sx={{
                    cursor: canResend ? "pointer" : "not-allowed",
                    opacity: canResend ? 1 : 0.5,
                  }}
                  disabled={!canResend}
                >
                  {resendCountdown > 0
                    ? `Resend code (${resendCountdown}s)`
                    : "Resend verification code"}
                </Link>
              </span>
            </Tooltip>
          </Grid>

          <Grid item>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => navigate("/auth/sign-in")}
            >
              Back to Sign In
            </Link>
          </Grid>
        </Grid>
      </form>
    </SignInLayout>
  );
};

export default EmailConfirmation;
